/*
 * Navbar — H-Plate public site navigation.
 *
 * Pages with a dark photo header pass `overlay`: the bar starts transparent
 * with light text and turns solid once the page scrolls. Every other page
 * gets the solid bar from the start, so the wordmark is always readable.
 */
import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { Facebook, Instagram, Menu, X } from "lucide-react";
import DownloadAppDialog, { openDownloadDialog } from "@/components/DownloadAppDialog";
import { FACEBOOK_URL, INSTAGRAM_URL } from "@/lib/socialLinks";

const NAV_LINKS = [
  { href: "/#features", label: "The app", anchor: true },
  { href: "/#routes", label: "Routes", anchor: true },
  { href: "/#drivers", label: "Drivers", anchor: true },
  { href: "/advertisers", label: "Advertise" },
  { href: "/support", label: "Support" },
];

/**
 * Site-wide top navigation.
 * @param overlay start transparent over a dark hero image
 */
export default function Navbar({ overlay = false }: { overlay?: boolean }) {
  const [location] = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = !overlay || scrolled || mobileOpen;

  const linkClass = (active: boolean) =>
    `px-3.5 py-2 rounded-lg text-[15px] font-medium transition-colors no-underline ${
      solid
        ? active
          ? "text-emerald-800 bg-emerald-50"
          : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
        : active
          ? "text-white bg-white/15"
          : "text-white/85 hover:text-white hover:bg-white/10"
    }`;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        solid
          ? "bg-white/95 backdrop-blur-md border-b border-slate-200/80"
          : "bg-gradient-to-b from-black/40 to-transparent border-b border-transparent"
      }`}
    >
      <nav className="container flex items-center justify-between h-16 md:h-[72px]">
        <Link href="/" className="flex items-center gap-2.5 no-underline" aria-label="H-Plate home">
          <img
            src="/logo-transparent.png"
            alt=""
            width={40}
            height={40}
            className="w-9 h-9 md:w-10 md:h-10 object-contain"
          />
          <span
            className={`font-display text-[26px] leading-none tracking-tight transition-colors ${
              solid ? "text-slate-900" : "text-white"
            }`}
          >
            H-Plate
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((link) =>
            link.anchor ? (
              <a key={link.href} href={link.href} className={linkClass(false)}>
                {link.label}
              </a>
            ) : (
              <Link key={link.href} href={link.href} className={linkClass(location === link.href)}>
                {link.label}
              </Link>
            )
          )}
          <button
            onClick={() => openDownloadDialog("general")}
            className={`ml-3 inline-flex items-center px-5 h-10 rounded-lg text-[15px] font-semibold border-0 cursor-pointer transition-colors ${
              solid
                ? "bg-emerald-700 text-white hover:bg-emerald-800"
                : "bg-white text-emerald-900 hover:bg-emerald-50"
            }`}
          >
            Get the app
          </button>
        </div>

        <button
          className={`md:hidden -mr-2 p-2 rounded-lg transition-colors ${
            solid ? "text-slate-900 hover:bg-slate-100" : "text-white hover:bg-white/10"
          }`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {mobileOpen && (
        <div className="md:hidden border-t border-slate-200 bg-white">
          <div className="container py-3 flex flex-col">
            {NAV_LINKS.map((link) =>
              link.anchor ? (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="px-2 py-3.5 text-base font-medium text-slate-700 border-b border-slate-100 no-underline"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className={`px-2 py-3.5 text-base font-medium border-b border-slate-100 no-underline ${
                    location === link.href ? "text-emerald-800" : "text-slate-700"
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}
            <button
              onClick={() => {
                setMobileOpen(false);
                openDownloadDialog("general");
              }}
              className="mt-4 mb-2 h-12 rounded-lg bg-emerald-700 text-white text-base font-semibold border-0 cursor-pointer"
            >
              Get the app
            </button>
            <div className="flex items-center justify-center gap-3 pt-2 pb-3">
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex flex-1 h-11 items-center justify-center gap-2 rounded-lg border border-slate-200 text-[15px] font-medium text-slate-700 no-underline"
              >
                <Instagram className="w-4 h-4" aria-hidden="true" />
                Instagram
              </a>
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex flex-1 h-11 items-center justify-center gap-2 rounded-lg border border-slate-200 text-[15px] font-medium text-slate-700 no-underline"
              >
                <Facebook className="w-4 h-4" aria-hidden="true" />
                Facebook
              </a>
            </div>
          </div>
        </div>
      )}
      <DownloadAppDialog />
    </header>
  );
}
